<template>
    <main class="subscription-page">
        <header class="subscription-topbar">
            <button class="brand" type="button" @click="goToAccount">
                <img src="https://swiftinvoicewebsite.s3-us-west-2.amazonaws.com/tab_logo.ico" alt="">
                <span>Swift Invoice</span>
            </button>
            <button class="close-button" type="button" aria-label="Back to account" @click="goToAccount">
                <i class="el-icon-close" aria-hidden="true"></i>
            </button>
        </header>

        <section class="subscription-intro">
            <h1>Professional Invoice Generator <span>for Small Business</span></h1>
        </section>

        <section class="plan-comparison" aria-label="Swift Invoice plans">
            <article
                v-for="plan in plans"
                :key="plan.name"
                class="plan-lane"
                :class="{
                    'is-featured': plan.name === 'Pro',
                    'is-current': currentPlan === plan.name,
                    'is-highlighted': selectedPlan === plan.name && currentPlan !== plan.name
                }"
            >
                <header class="plan-lane-heading">
                    <div v-if="plan.name === 'Pro'" class="plan-flight-visual" aria-hidden="true">
                        <svg viewBox="0 0 240 240" fill="none">
                            <defs>
                                <clipPath id="pricing-flight-stage">
                                    <rect width="240" height="180"/>
                                </clipPath>
                                <filter id="pricing-flight-light" x="-100%" y="-100%" width="300%" height="300%">
                                    <feGaussianBlur stdDeviation="20"/>
                                </filter>
                            </defs>
                            <ellipse cx="185" cy="95" rx="48" ry="62" fill="#efab72" opacity="0.3" filter="url(#pricing-flight-light)"/>
                            <g clip-path="url(#pricing-flight-stage)">
                                <image
                                    class="paper-plane"
                                    :href="require('../../assets/subscription-paper-plane.png')"
                                    x="48" y="40" width="172" height="115"
                                />
                            </g>
                        </svg>
                    </div>
                    <div class="plan-lane-meta">
                        <span v-if="currentPlan === plan.name" class="status-label">Current plan</span>
                        <span v-else-if="plan.discountLabel" class="saving-label">{{plan.discountLabel}}</span>
                    </div>
                    <h2>{{plan.title}}</h2>
                    <div class="plan-lane-price">
                        <span v-if="plan.originalPrice" class="original-price">USD {{plan.originalPrice}}</span>
                        <span v-else class="original-price is-empty" aria-hidden="true">USD 0</span>
                        <div class="current-price">
                            <span>USD</span>
                            <strong>{{plan.price}}</strong>
                            <span>{{plan.priceNote}}</span>
                        </div>
                    </div>
                </header>

                <footer class="plan-lane-actions">
                    <button
                        v-if="plan.name === 'Free'"
                        class="plan-button secondary-button"
                        type="button"
                        @click="goToInvoices"
                    >
                        Continue with Free
                    </button>
                    <button
                        v-else-if="canSubscribe(plan.name)"
                        class="plan-button"
                        type="button"
                        @click="openCheckout(plan)"
                    >
                        Subscribe
                    </button>
                    <button v-else class="plan-button" type="button" disabled>
                        Current plan
                    </button>
                </footer>

                <ul class="plan-feature-list">
                    <li v-for="feature in plan.features" :key="feature">
                        <i class="el-icon-check" aria-hidden="true"></i>
                        <span>{{feature}}</span>
                    </li>
                    <li :class="{'is-unavailable': !plan.multipleBusinesses}"
                        :aria-label="plan.multipleBusinesses ? 'Multiple businesses included' : 'Multiple businesses not included'">
                        <i :class="plan.multipleBusinesses ? 'el-icon-check' : 'el-icon-close'" aria-hidden="true"></i>
                        <span>Multiple businesses</span>
                    </li>
                </ul>


            </article>
        </section>

    <transition name="checkout-modal">
      <div
        v-show="checkoutPlan"
                class="checkout-overlay"
                role="presentation"
                @click.self="closeCheckout"
            >
                <section
                    class="checkout-dialog"
                    role="dialog"
                    aria-modal="true"
                    :aria-label="checkoutPlan ? checkoutPlan.title + ' secure checkout' : 'Secure checkout'"
                >
                    <header class="checkout-heading">
                        <div>
                            <div class="eyebrow">Secure checkout</div>
                            <h2>{{checkoutPlan ? checkoutPlan.title : ''}}</h2>
                        </div>
                        <button class="close-checkout" type="button" aria-label="Close checkout" @click="closeCheckout">
                            <i class="el-icon-close" aria-hidden="true"></i>
                        </button>
                    </header>
                    <div class="checkout-body" aria-live="polite">
                        <div v-if="checkoutError" class="checkout-error" role="alert">{{checkoutError}}</div>
                        <div class="checkout-frame" :class="{'is-hidden': checkoutError}">
                            <div v-if="checkoutLoading" class="checkout-loading" role="status" aria-label="Loading secure checkout">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                            <div id="fsc-embedded-checkout-container"></div>
                        </div>
                    </div>
                </section>
            </div>
        </transition>

        <p class="support-note">
            You can cancel your subscription at any time by email.
            <a href="mailto:zn978740431@gmail.com?subject=Web%20Subscription%20Issue">Contact support</a>
        </p>
    </main>
</template>

<script>
import {getFastSpringPageContext} from '@/utils/subscription'
import {reportHandledError} from '@/utils/sentry'

const FASTSPRING_SCRIPT_ID = 'fsc-api';
const FASTSPRING_SCRIPT_URL = 'https://sbl.onfastspring.com/sbl/1.0.7/fastspring-builder.min.js';
const FASTSPRING_STOREFRONT = process.env.NODE_ENV === 'production'
    ? 'swiftinvoice.onfastspring.com/embedded-subscription'
    : 'swiftinvoice.test.onfastspring.com/embedded-subscription';
const FASTSPRING_PRO_PRODUCT_PATH = 'monthlypro-test';

export default {
    name: 'Subscription',
    data() {
        const pageContext = getFastSpringPageContext();
        return {
            currentPlan: pageContext.currentPlan,
            selectedPlan: pageContext.selectedPlan,
            subscriptionUserId: pageContext.userId,
            checkoutPlan: null,
            checkoutLoading: false,
            checkoutError: '',
            bodyOverflowBeforeCheckout: '',
            fastSpringDataCallback: null,
            fastSpringErrorCallback: null,
            checkoutRequestId: 0,
            plans: [
                {
                    name: 'Free',
                    title: 'Free',
                    price: '0',
                    priceNote: 'forever',
                    features: ['Manage clients and items', 'View and edit existing documents'],
                    multipleBusinesses: false,
                    productPath: ''
                },
                {
                    name: 'Pro',
                    title: 'Annual Pro',
                    price: '29.9',
                    originalPrice: '49.9',
                    discountLabel: 'Save 40%',
                    priceNote: 'per year',
                    features: ['Unlimited invoices, estimates, and purchase orders', 'Item Inventory', 'Shared access on web and mobile'],
                    multipleBusinesses: false,
                    productPath: FASTSPRING_PRO_PRODUCT_PATH
                },
                {
                    name: 'Premium',
                    title: 'Annual Premium',
                    price: '49.9',
                    originalPrice: '79.9',
                    discountLabel: 'Save 40%',
                    priceNote: 'per year',
                    features: ['Unlimited invoices, estimates, and purchase orders', 'Item Inventory', 'Shared access on web and mobile'],
                    multipleBusinesses: true,
                    productPath: 'yearly-premium'
                }
            ]
        };
    },
    mounted() {
        document.addEventListener('keydown', this.handleKeydown);
    },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
    this.checkoutRequestId += 1;
    this.clearFastSpringCheckout();
    document.body.style.overflow = this.bodyOverflowBeforeCheckout;
        if (window.fastSpringSubscriptionDataCallback === this.fastSpringDataCallback) {
            delete window.fastSpringSubscriptionDataCallback;
        }
        if (window.fastSpringSubscriptionErrorCallback === this.fastSpringErrorCallback) {
            delete window.fastSpringSubscriptionErrorCallback;
        }
    },
    methods: {
    canSubscribe(planName) {
      return planName !== this.currentPlan;
        },
        goToAccount() {
            this.$router.push('/account');
        },
        goToInvoices() {
            this.$router.push('/invoice');
        },
        handleKeydown(event) {
            if (event.key === 'Escape' && this.checkoutPlan) {
                this.closeCheckout();
            }
        },
    closeCheckout() {
      this.checkoutRequestId += 1;
      this.clearFastSpringCheckout();
      this.checkoutLoading = false;
      this.checkoutPlan = null;
      this.checkoutError = '';
      document.body.style.overflow = this.bodyOverflowBeforeCheckout;
    },
    openCheckout(plan) {
      this.checkoutRequestId += 1;
      const requestId = this.checkoutRequestId;
      this.clearFastSpringCheckout();
      this.selectedPlan = plan.name;
      this.checkoutPlan = plan;
            this.checkoutLoading = true;
            this.checkoutError = '';
            this.bodyOverflowBeforeCheckout = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            if (this.subscriptionUserId === '') {
                this.reportSubscriptionError(new Error('subscription userId is empty'), 'open_checkout_missing_user');
                this.checkoutLoading = false;
                this.checkoutError = 'The subscription account could not be identified. Please return to Account and try again.';
                return;
            }
            this.$nextTick(() => {
                this.loadFastSpring(plan, requestId).catch((error) => {
                    if (requestId !== this.checkoutRequestId) {
                        return;
                    }
                    this.reportSubscriptionError(error, 'load_fastspring', {
                        planName: plan.name,
                        productPath: plan.productPath
                    });
                    this.checkoutLoading = false;
                    this.checkoutError = 'The secure checkout could not be loaded. Please try again.';
        });
      });
    },
    clearFastSpringCheckout() {
      if (
        window.fastspring &&
        window.fastspring.builder &&
        typeof window.fastspring.builder.reset === 'function'
      ) {
        try {
          window.fastspring.builder.reset();
        } catch (error) {
          this.reportSubscriptionError(error, 'clear_checkout');
        }
      }
      delete window.fscSession;
    },
    checkoutSession(plan) {
            return {
                reset: true,
                products: [{path: plan.productPath, quantity: 1}],
                tags: {userId: this.subscriptionUserId}
            };
        },
        loadFastSpring(plan, requestId) {
            const session = this.checkoutSession(plan);
            this.fastSpringDataCallback = () => {
                if (requestId !== this.checkoutRequestId) {
                    return;
                }
                this.checkoutLoading = false;
            };
            this.fastSpringErrorCallback = (error) => {
                if (requestId !== this.checkoutRequestId) {
                    return;
                }
                this.reportSubscriptionError(error || new Error('FastSpring checkout error callback'), 'fastspring_error_callback', {
                    planName: plan.name,
                    productPath: plan.productPath
                });
                this.checkoutLoading = false;
                this.checkoutError = 'The secure checkout could not be loaded. Please try again.';
            };
            window.fastSpringSubscriptionDataCallback = this.fastSpringDataCallback;
            window.fastSpringSubscriptionErrorCallback = this.fastSpringErrorCallback;

            if (window.fastspring && window.fastspring.builder) {
                try {
                    window.fastspring.builder.push(session);
                    return Promise.resolve();
                } catch (error) {
                    return Promise.reject(error);
                }
            }

            window.fscSession = session;
            const existingScript = document.getElementById(FASTSPRING_SCRIPT_ID);
            if (existingScript) {
                return new Promise((resolve, reject) => {
                    existingScript.addEventListener('load', resolve, {once: true});
                    existingScript.addEventListener('error', () => reject(new Error('FastSpring script failed to load')), {once: true});
                });
            }

            return new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.id = FASTSPRING_SCRIPT_ID;
                script.src = FASTSPRING_SCRIPT_URL;
                script.type = 'text/javascript';
                script.dataset.storefront = FASTSPRING_STOREFRONT;
                script.dataset.dataCallback = 'fastSpringSubscriptionDataCallback';
                script.dataset.errorCallback = 'fastSpringSubscriptionErrorCallback';
                script.onload = resolve;
                script.onerror = () => reject(new Error('FastSpring script failed to load'));
                document.head.appendChild(script);
            });
        },
        reportSubscriptionError(error, operation, context) {
            reportHandledError(error, {
                level: 'error',
                module: 'subscription',
                operation: operation,
                context: Object.assign({
                    currentPlan: this.currentPlan,
                    selectedPlan: this.selectedPlan,
                    userId: this.subscriptionUserId,
                    storefront: FASTSPRING_STOREFRONT
                }, context || {})
            });
        }
    }
};
</script>

<style scoped>
.subscription-page {
    min-height: 100vh;
    box-sizing: border-box;
    padding: 28px 40px 36px;
    overflow-x: hidden;
    background: #080a0f;
    color: #f2f1ed;
    font-family: var(--ui-font-family);
    letter-spacing: 0;
    -webkit-font-smoothing: antialiased;
}

.subscription-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 1240px;
    min-height: 48px;
    margin: 0 auto;
}

.brand,
.close-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    color: inherit;
    background: transparent;
    font: inherit;
    cursor: pointer;
}

.brand {
    gap: 12px;
    padding: 0;
    font-size: 20px;
    font-weight: 500;
}

.brand img {
    width: 32px;
    height: 32px;
    object-fit: contain;
    border-radius: 8px;
}

.close-button {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    color: #9d9fa8;
    font-size: 20px;
    transition: background 160ms ease, color 160ms ease;
}

.close-button:hover {
    color: #f2f1ed;
    background: #1a1c24;
}

.subscription-intro {
    max-width: 1000px;
    margin: 64px auto 48px;
    text-align: center;
}

.subscription-intro h1 {
    margin: 0;
    color: #f2f1ed;
    font-size: clamp(34px, 3.5vw, 56px);
    font-weight: 500;
    line-height: 1.12;
    letter-spacing: -0.035em;
}

.subscription-intro h1 span {
    display: block;
}

.plan-comparison {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    align-items: stretch;
    gap: 24px;
    max-width: 1240px;
    margin: 0 auto;
}

.plan-lane {
    position: relative;
    min-width: 0;
    display: flex;
    flex-direction: column;
    padding: 28px;
    overflow: hidden;
    border: 1px solid #292c35;
    border-radius: 18px;
    background: #101219;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.025);
}

.plan-lane.is-current {
    border-color: #555963;
}

.plan-lane.is-highlighted {
    border-color: #8c7158;
}

.plan-lane.is-featured {
    border-color: #b38b63;
    background: radial-gradient(ellipse at 100% 0%, rgba(201, 122, 65, 0.32), transparent 65%),
        linear-gradient(180deg, #211810, #100e0c 76%);
    box-shadow: inset 0 1px 0 rgba(255, 226, 191, 0.5), 0 12px 40px rgba(0, 0, 0, 0.18);
}

.plan-lane-heading {
    position: relative;
    min-height: 220px;
}

.plan-lane-meta {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    min-height: 24px;
}

.status-label,
.saving-label {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    box-sizing: border-box;
    padding: 0 8px;
    border: 1px solid #3c3f48;
    border-radius: 6px;
    background: #1c1e26;
    color: #d4d5da;
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
}

.is-featured .status-label,
.is-featured .saving-label {
    border-color: #8b6240;
    background: #4b3020;
    color: #f2cda6;
}

.plan-lane h2 {
    position: relative;
    z-index: 1;
    margin: 12px 0 0;
    color: #f2f1ed;
    font-size: 23px;
    font-weight: 500;
    line-height: 32px;
    letter-spacing: -0.015em;
}

.plan-lane-price {
    position: relative;
    z-index: 1;
    width: max-content;
    max-width: 100%;
    margin-top: 20px;
}

.original-price {
    display: block;
    min-height: 20px;
    color: #92949d;
    font-size: 13px;
    line-height: 20px;
    text-decoration: line-through;
    text-decoration-thickness: 1px;
}

.original-price.is-empty {
    visibility: hidden;
}

.current-price {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-top: 4px;
    font-variant-numeric: tabular-nums;
}

.current-price strong {
    color: #f2f1ed;
    font-size: 64px;
    font-weight: 400;
    line-height: 1;
    letter-spacing: -0.04em;
}

.current-price span:first-child {
    color: #b9bbc3;
    font-size: 14px;
    line-height: 24px;
}

.current-price span:last-child {
    color: #9d9fa8;
    font-size: 14px;
    line-height: 24px;
    white-space: nowrap;
}

.plan-lane-actions {
    position: relative;
    z-index: 1;
    min-height: 48px;
    margin-top: 20px;
}

.plan-button {
    width: 100%;
    min-height: 48px;
    padding: 12px 16px;
    border: 1px solid #373a44;
    border-radius: 10px;
    background: #20222b;
    color: #f2f1ed;
    font: inherit;
    font-size: 14px;
    font-weight: 500;
    line-height: 22px;
    cursor: pointer;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.035);
    transition: background 160ms ease, border-color 160ms ease, color 160ms ease;
}

.plan-button:hover {
    border-color: #5a5d68;
    background: #2b2e38;
}

.is-featured .plan-button {
    border-color: #f0d1ae;
    background: linear-gradient(110deg, #ebc096, #faebd5);
    color: #312217;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5);
}

.is-featured .plan-button:hover {
    border-color: #fff1db;
    background: #fae6cc;
}

.plan-button:disabled {
    cursor: default;
    opacity: 0.65;
}

.plan-feature-list {
    position: relative;
    z-index: 1;
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 224px;
    margin: 24px 0 0;
    padding: 0;
    list-style: none;
}

.plan-feature-list li {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 56px;
    box-sizing: border-box;
    padding: 14px 0;
    color: #c4c5cc;
    font-size: 14px;
    line-height: 24px;
}

.plan-feature-list li:last-child {
    margin-top: auto;
}

.plan-feature-list li i {
    flex: 0 0 16px;
    width: 16px;
    color: #969aa6;
    font-size: 16px;
    text-align: center;
}

.is-featured .plan-feature-list li i {
    color: #e5b98e;
}

.plan-feature-list .is-unavailable,
.plan-feature-list .is-unavailable i {
    color: #898c97;
}

.plan-flight-visual {
    position: absolute;
    top: -20px;
    right: -28px;
    width: 208px;
    height: 228px;
    pointer-events: none;
}

.plan-flight-visual svg {
    display: block;
    width: 100%;
    height: 100%;
    overflow: visible;
}

.paper-plane {
    animation: paper-plane-flight 7s linear infinite;
}

.support-note {
    max-width: 1240px;
    margin: 28px auto 0;
    color: #92949e;
    font-size: 13px;
    line-height: 24px;
    text-align: center;
}

.support-note a {
    margin-left: 4px;
    color: #d9b58f;
    text-decoration: underline;
    text-underline-offset: 4px;
}

.subscription-page :focus-visible {
    outline: 2px solid #e7bb91;
    outline-offset: 4px;
}

@keyframes paper-plane-flight {
    0%, 34% {
        opacity: 1;
        transform: translate(0, 0);
        animation-timing-function: cubic-bezier(0.45, 0, 0.8, 0.3);
    }
    52% {
        opacity: 1;
        transform: translate(240px, -174px);
    }
    53% {
        opacity: 0;
        transform: translate(240px, -174px);
    }
    54%, 56% {
        opacity: 0;
        transform: translate(-240px, 174px);
        animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
    }
    62% {
        opacity: 1;
    }
    82%, 100% {
        opacity: 1;
        transform: translate(0, 0);
    }
}

@media (min-width: 1500px) and (min-height: 900px) {
    .subscription-intro { margin-top: 76px; margin-bottom: 48px; }
    .plan-lane { padding: 32px; }
}

@media (max-width: 1000px) {
    .subscription-page { padding: 24px; }
    .plan-comparison { gap: 16px; }
    .plan-lane { padding: 24px 20px; }
    .plan-lane h2 { font-size: 21px; }
    .current-price strong { font-size: 56px; }
    .plan-flight-visual { right: -20px; width: 172px; }
}

@media (max-width: 960px) {
    .subscription-page { padding: 20px 20px 28px; }
    .subscription-topbar { max-width: 480px; }
    .subscription-intro { margin: 40px auto 32px; }
    .subscription-intro h1 { font-size: 34px; }
    .brand { font-size: 18px; }
    .plan-comparison { grid-template-columns: minmax(0, 1fr); max-width: 480px; gap: 20px; }
    .plan-lane { padding: 28px; }
    .plan-lane-heading { min-height: 212px; }
    .plan-lane h2 { font-size: 23px; }
    .current-price strong { font-size: 64px; }
    .plan-flight-visual { right: -28px; width: 200px; }
    .plan-feature-list { min-height: 0; }
    .support-note { max-width: 480px; margin-top: 24px; }
}

@media (max-width: 400px) {
    .current-price strong { font-size: 56px; }
}

@media (prefers-reduced-motion: reduce) {
    .paper-plane { animation: none; }
    .plan-button,
    .close-button { transition: none; }
}

/* Existing FastSpring checkout presentation. */
.close-checkout {
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    cursor: pointer;
}

.eyebrow {
    color: #0d7df7;
    font-size: var(--ui-font-small);
    font-weight: 600;
    letter-spacing: 0;
    text-transform: uppercase;
}

.checkout-overlay {
    position: fixed;
    inset: 0;
    z-index: 4000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: rgba(13, 25, 43, 0.66);
    backdrop-filter: blur(3px);
}

.checkout-dialog {
    width: min(1060px, 100%);
    max-height: calc(100vh - 40px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid #d7e0ea;
    border-radius: var(--ui-radius-panel);
    background: #ffffff;
    box-shadow: 0 26px 80px rgba(9, 20, 36, 0.3);
}

.checkout-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    flex: 0 0 auto;
    min-height: 78px;
    padding: 16px 22px;
    border-bottom: 1px solid #e1e7ef;
}

.checkout-heading h2 {
    margin: 4px 0 0;
    color: #17212f;
    font-size: 22px;
}

.close-checkout {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    border: 1px solid #d7e0ea;
    border-radius: var(--ui-radius-control);
    background: #ffffff;
    color: #526071;
    font-size: 19px;
}

.close-checkout:hover,
.close-checkout:focus {
    border-color: #aeb9c7;
    background: #eef3f8;
}

.checkout-body {
    min-height: 320px;
    padding: 22px;
    overflow: auto;
}

.checkout-frame {
    position: relative;
    min-height: 590px;
}

.checkout-frame.is-hidden {
    display: none;
}

.checkout-loading {
    position: absolute;
    inset: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: #657282;
    background: rgba(255, 255, 255, 0.96);
}

.checkout-loading i {
    color: #0d7df7;
    font-size: 22px;
}

#fsc-embedded-checkout-container {
    width: 100%;
    min-height: 590px;
}

.checkout-error {
    padding: 18px;
    border: 1px solid #f1b5b5;
    border-radius: var(--ui-radius-control);
    background: #fff5f5;
    color: #a42828;
}

.checkout-modal-enter-active,
.checkout-modal-leave-active {
    transition: opacity 160ms ease;
}

.checkout-modal-enter-active .checkout-dialog,
.checkout-modal-leave-active .checkout-dialog {
    transition: transform 160ms ease, opacity 160ms ease;
}

.checkout-modal-enter,
.checkout-modal-leave-to {
    opacity: 0;
}

.checkout-modal-enter .checkout-dialog,
.checkout-modal-leave-to .checkout-dialog {
    opacity: 0;
    transform: translateY(10px);
}

@media (max-width: 600px) {

    .checkout-overlay {
        padding: 10px;
    }

    .checkout-dialog {
        max-height: calc(100vh - 20px);
    }

    .checkout-heading {
        min-height: 70px;
        padding: 14px 16px;
    }

    .checkout-body {
        padding: 14px;
    }

    .checkout-frame,
#fsc-embedded-checkout-container {
        min-height: 520px;
    }
}

.close-checkout {
    border: 1px solid #d5deea;
    border-radius: var(--ui-radius-control);
    background: #ffffff;
    color: #5f6c7c;
    transition: border-color 160ms ease, background 160ms ease, color 160ms ease;
}

.close-checkout:hover,
.close-checkout:focus-visible {
    border-color: #92a2b6;
    background: #f8fafc;
    color: #172033;
    outline: none;
}

.checkout-overlay {
    padding: 24px;
    background: rgba(18, 28, 43, 0.72);
    backdrop-filter: blur(6px);
}

.checkout-dialog {
    width: min(1100px, 100%);
    max-height: calc(100vh - 48px);
    border: 1px solid #cfd9e5;
    border-radius: var(--ui-radius-panel);
    box-shadow: 0 28px 90px rgba(8, 18, 34, 0.35);
}

.checkout-heading {
    min-height: 82px;
    padding: 17px 24px;
    background: #f7f9fc;
}

.checkout-heading h2 {
    margin: 3px 0 0;
    color: #172033;
    font-size: 23px;
    letter-spacing: 0;
}

.eyebrow {
    color: #1677ee;
    font-size: 12px;
    letter-spacing: 0;
}

.checkout-body {
    padding: 24px;
}

.checkout-frame,
#fsc-embedded-checkout-container {
    min-height: 590px;
}

.checkout-loading {
    flex-direction: column;
    gap: 14px;
    padding: 48px;
    background: #ffffff;
}

.checkout-loading span {
    display: block;
    width: min(680px, 86%);
    height: 72px;
    border-radius: var(--ui-radius-control);
    background: #edf2f7;
    animation: checkout-pulse 1.1s ease-in-out infinite alternate;
}

.checkout-loading span:nth-child(2) {
    width: min(520px, 72%);
}

.checkout-loading span:nth-child(3) {
    width: min(600px, 80%);
}

.checkout-error {
    border-radius: var(--ui-radius-control);
}

@keyframes checkout-pulse {
    from {
        opacity: 0.55;
    }
    to {
        opacity: 1;
    }
}

@media (max-width: 600px) {

    .checkout-overlay {
        padding: 8px;
    }

    .checkout-dialog {
        max-height: calc(100vh - 16px);
    }

    .checkout-heading {
        min-height: 70px;
        padding: 13px 14px;
    }

    .checkout-body {
        padding: 12px;
    }

    .checkout-loading {
        padding: 28px 14px;
    }

    .checkout-frame,
#fsc-embedded-checkout-container {
        min-height: 520px;
    }
}

@media (prefers-reduced-motion: reduce) {

    .checkout-modal-enter-active,
.checkout-modal-leave-active,
.checkout-modal-enter-active .checkout-dialog,
.checkout-modal-leave-active .checkout-dialog {
        transition: none;
    }

    .checkout-loading span {
        animation: none;
    }
}

.checkout-dialog { color: #263653; }
</style>
